import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { TrendPoint } from '../../utils/api/natureDrApi';
import './TrendChart.css';

interface TrendChartProps {
  trends: TrendPoint[];
  markerName: string;
  referenceMin?: number | null;
  referenceMax?: number | null;
  unit?: string;
}

const TrendChart: React.FC<TrendChartProps> = ({
  trends,
  markerName,
  referenceMin,
  referenceMax,
  unit = '',
}) => {
  const chartData = trends
    .filter(t => t.markers[markerName])
    .map(t => ({
      date: new Date(t.date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
      }),
      value: t.markers[markerName].value,
      status: t.markers[markerName].status,
    }));

  if (chartData.length === 0) {
    return (
      <div className="trend-chart-empty">
        <p>No trend data available for {markerName}</p>
      </div>
    );
  }

  const values = chartData.map(d => d.value);
  const minVal = Math.min(...values, referenceMin ?? Infinity);
  const maxVal = Math.max(...values, referenceMax ?? -Infinity);
  const padding = (maxVal - minVal) * 0.15 || 5;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'high': return '#e74c3c';
      case 'low': return '#f39c12';
      default: return '#5D8F77';
    }
  };

  const latestStatus = chartData[chartData.length - 1]?.status || 'normal';

  return (
    <div className="trend-chart-wrapper">
      <div className="trend-chart-title">
        <span className="trend-marker-name">{markerName}</span>
        {unit && <span className="trend-unit">({unit})</span>}
        <span className={`trend-latest-badge ${latestStatus}`}>
          {chartData[chartData.length - 1]?.value} {unit}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-primary-alpha-10)" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            tickLine={false}
          />
          <YAxis
            domain={[minVal - padding, maxVal + padding]}
            tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-primary-alpha-20)',
              borderRadius: '8px',
              fontSize: '0.9rem',
            }}
            formatter={(value) => [`${value} ${unit}`, markerName]}
          />
          {referenceMin !== null && referenceMin !== undefined && (
            <ReferenceLine
              y={referenceMin}
              stroke="#f39c12"
              strokeDasharray="5 5"
              label={{ value: 'Min', position: 'left', fontSize: 11, fill: '#f39c12' }}
            />
          )}
          {referenceMax !== null && referenceMax !== undefined && (
            <ReferenceLine
              y={referenceMax}
              stroke="#e74c3c"
              strokeDasharray="5 5"
              label={{ value: 'Max', position: 'left', fontSize: 11, fill: '#e74c3c' }}
            />
          )}
          <Line
            type="monotone"
            dataKey="value"
            stroke={getStatusColor(latestStatus)}
            strokeWidth={2.5}
            dot={{ r: 5, fill: 'var(--color-surface)', strokeWidth: 2.5 }}
            activeDot={{ r: 7 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendChart;
